import { useMemo } from 'react';

import type { AnyComment, Comment, CommentAuthor, CommentReply } from '../model/types';

import { useUserScope } from '@/hooks/useUserScope';
import { getCurrentUserV2QueryOptions } from '@/services/getCurrentUserV2';
import { toNumericUserId } from '@/util/authTokens';
import { queryClient } from '@/util/queryClient';

const transformAvatar = (
  avatar:
    | {
        Location128px?: string;
        Bucket?: string;
        ETag?: string;
        Key?: string;
        Location?: string;
        isPublic?: boolean;
      }
    | null
    | undefined,
  isAnonymous: boolean
): CommentAuthor['avatar'] => {
  if (isAnonymous || !avatar) {
    return undefined;
  }

  return {
    Location128px: avatar.Location128px || undefined,
    Bucket: avatar.Bucket || undefined,
    ETag: avatar.ETag || undefined,
    Key: avatar.Key || undefined,
    Location: avatar.Location || undefined,
    ServerSideEncryption: undefined,
    isPublic: avatar.isPublic || undefined,
  };
};

/**
 * Hook for creating optimistic comment objects.
 *
 * Converts userId from userIdAtom to number, returning 0 if userId is a Cognito UUID.
 *
 * Anonymous comments:
 * - When isAnonymous is true, anonymizes user data (firstName/lastName set to null, avatar removed, state hidden)
 * - Sets isAnonymous flag on both top-level comments and replies
 * - Optimistic comment displays immediately with anonymized data before API response
 */
export function useOptimisticComment() {
  const { userId: dbUserId } = useUserScope();

  const loggedInUserId = toNumericUserId(dbUserId) ?? 0;

  const loggedInUser = queryClient.getQueryData(
    getCurrentUserV2QueryOptions(loggedInUserId.toString()).queryKey
  );

  return useMemo(
    () =>
      ({
        body,
        parentId = null,
        replyToId = null,
        isAnonymous = false,
      }: {
        body: string;
        parentId?: number | null;
        replyToId?: number | null;
        isAnonymous: boolean;
      }): AnyComment => {
        const baseComment = {
          id: Date.now(),
          body,
          parentId,
          replyToId,
          createdAt: new Date().toISOString(),
          userId: loggedInUserId,
          user: {
            id: loggedInUserId,
            firstName: isAnonymous ? null : loggedInUser?.firstName || '',
            lastName: isAnonymous ? null : loggedInUser?.lastName || '',
            identifiesAsDoctor: loggedInUser?.identifiesAsDoctor || false,
            state: isAnonymous ? null : loggedInUser?.state || null,
            userType: loggedInUser?.userType || '',
            avatar: transformAvatar(loggedInUser?.avatar, isAnonymous),
          },
          likes: 0,
          liked: false,
        };

        if (!replyToId && !parentId) {
          return {
            ...baseComment,
            isAnonymous,
            replies: [],
          } as Comment;
        }

        return {
          ...baseComment,
          isAnonymous,
        } as CommentReply;
      },
    [loggedInUserId, loggedInUser]
  );
}
